import { BrowserWalletGrantStore, type WalletGrantStore } from './wallet-grant-store';
import { WalletOperation } from './wallet-operation';

export const WALLET_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

export const WALLET_ACTIVITY_THROTTLE_MS = 10 * 1000;

export enum WalletGrantExpiry {
  NotExpired = 'not-expired',
  RemovedShared = 'removed-shared',
  LockedHere = 'locked-here',
}

export interface WalletIdentity {
  gammaUserId: string;
  privyUserId: string;
}

// Persisted across reloads and tabs. It holds no token: a grant unlocks nothing unless Privy is
// signed in as the same user, which the provider verifies before treating it as unlocked.
export interface WalletUnlockGrant extends WalletIdentity {
  lastActivityAt: number;
}

export const walletGrantExpiresAt = (grant: WalletUnlockGrant): number =>
  grant.lastActivityAt + WALLET_IDLE_TIMEOUT_MS;

const isLive = (grant: WalletUnlockGrant, now: number): boolean =>
  now >= grant.lastActivityAt && now < walletGrantExpiresAt(grant);

const isSameGrant = (left: WalletUnlockGrant, right: WalletUnlockGrant): boolean =>
  left.gammaUserId === right.gammaUserId &&
  left.privyUserId === right.privyUserId &&
  left.lastActivityAt === right.lastActivityAt;

export class WalletUnlockSession {
  private owner: string | null = null;
  private generation = 0;
  private grant: WalletUnlockGrant | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly store: WalletGrantStore = new BrowserWalletGrantStore()) {
    store.subscribe(this.syncFromStore);
  }

  getSnapshot = (): WalletUnlockGrant | null => this.grant;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  setOwner(owner: string | null): boolean {
    const previous = this.owner;

    if (previous === owner) {
      return false;
    }

    this.owner = owner;

    if (previous === null && owner !== null) {
      this.keepOrRestore(owner, Date.now());
      return false;
    }

    this.lock();
    return true;
  }

  lock(): void {
    this.store.clear();
    this.dropGrant();
  }

  // Keeps the shared grant: this tab's Privy state may only be stale, and other tabs stay unlocked.
  lockThisTab(): void {
    this.dropGrant();
  }

  resumeStoredGrant(now: number): boolean {
    const stored = this.grant ? null : this.restorableGrant(now);

    if (stored) {
      this.adopt(stored);
    }

    return this.isUnlocked(now);
  }

  restoreBoundGrant(privyUserId: string, now: number): void {
    const stored = this.grant ? null : this.restorableGrant(now);

    if (stored?.privyUserId === privyUserId) {
      this.adopt(stored);
    }
  }

  beginAccess(): WalletOperation {
    const generation = this.generation;
    return new WalletOperation(() => this.generation === generation);
  }

  beginOperation(now: number): WalletOperation {
    this.touch(now);
    return this.beginAccess().narrow(() => this.isUnlocked(Date.now()));
  }

  unlock(identity: WalletIdentity, access: WalletOperation, now: number): boolean {
    if (!access.isCurrent() || this.owner !== identity.gammaUserId) {
      return false;
    }

    const grant = { ...identity, lastActivityAt: now };
    this.store.write(grant);
    this.adopt(grant);
    return true;
  }

  isUnlocked(now: number): boolean {
    const { grant } = this;
    return grant !== null && grant.gammaUserId === this.owner && isLive(grant, now);
  }

  touch(now: number): boolean {
    if (!this.grant || !this.isUnlocked(now)) {
      return false;
    }

    // Activity is written at most every 10 seconds to keep storage writes and cross-tab events rare.
    if (now - this.grant.lastActivityAt < WALLET_ACTIVITY_THROTTLE_MS) {
      return true;
    }

    const touched = { ...this.grant, lastActivityAt: now };
    this.store.write(touched);
    this.adopt(touched);
    return true;
  }

  expireIdleGrant(now: number): WalletGrantExpiry {
    if (!this.grant || this.isUnlocked(now)) {
      return WalletGrantExpiry.NotExpired;
    }

    const stored = this.store.read();

    if (stored && this.isNewerOwnedGrant(stored, now)) {
      this.adopt(stored);
      return WalletGrantExpiry.NotExpired;
    }

    // Only the tab that removes the shared grant ends the Privy session, so tabs never log it out twice.
    const removesShared = stored !== null && isSameGrant(stored, this.grant);

    if (removesShared) {
      this.store.clear();
    }

    this.dropGrant();
    return removesShared ? WalletGrantExpiry.RemovedShared : WalletGrantExpiry.LockedHere;
  }

  private keepOrRestore(owner: string, now: number): void {
    if (this.grant?.gammaUserId === owner) {
      return;
    }

    const stored = this.restorableGrant(now);

    if (stored) {
      this.adopt(stored);
      return;
    }

    this.lock();
  }

  private restorableGrant(now: number): WalletUnlockGrant | null {
    const stored = this.store.read();

    return stored && this.isNewerOwnedGrant(stored, now) ? stored : null;
  }

  private syncFromStore = (): void => {
    const stored = this.store.read();

    if (stored && this.isNewerOwnedGrant(stored, Date.now())) {
      this.adopt(stored);
      return;
    }

    if (this.grant && stored?.gammaUserId !== this.owner) {
      // Another tab locked or switched owner and already ended the Privy session; only lock here.
      this.dropGrant();
    }
  };

  private isNewerOwnedGrant(stored: WalletUnlockGrant, now: number): boolean {
    return (
      this.owner !== null &&
      stored.gammaUserId === this.owner &&
      isLive(stored, now) &&
      stored.lastActivityAt > this.ownedActivityAt()
    );
  }

  private ownedActivityAt(): number {
    if (this.grant?.gammaUserId !== this.owner) {
      return Number.NEGATIVE_INFINITY;
    }

    return this.grant.lastActivityAt;
  }

  private adopt(grant: WalletUnlockGrant): void {
    this.grant = grant;
    this.emit();
  }

  private dropGrant(): void {
    this.generation += 1;
    this.grant = null;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const walletUnlockSession = new WalletUnlockSession();
